import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, ExternalLink, User, Mail, FileText, Lock, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { useAuth } from '../lib/auth';

export default function Register() {
  const { user, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // If already authenticated, skip to package selection
  const showPackages = submitted || isAuthenticated();

  const benefits = [
    'Full credit report analysis',
    'Dispute letters to all 3 bureaus',
    '35-day progress updates',
    'Secure document portal',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: `${formData.firstName} ${formData.lastName}`,
      });
      toast.success('Account created! Please choose your package below.');
      setSubmitted(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Build Stripe URLs with prefilled email
  const getStripeUrl = (baseUrl) => {
    const email = encodeURIComponent(formData.email || user?.email || '');
    return `${baseUrl}?prefilled_email=${email}`;
  };

  const displayName = formData.firstName || user?.full_name?.split(' ')[0] || '';
  const displayEmail = formData.email || user?.email || '';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Toaster position="top-center" />
      
      {/* Left Side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-center">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <Shield className="h-10 w-10 text-amber-500" strokeWidth={1.5} />
          <div>
            <h1 className="font-heading text-2xl font-bold">Tamperis Solutions</h1>
            <p className="text-sm text-amber-500">From Faith to Freedom</p>
          </div>
        </Link>

        <h2 className="font-heading text-3xl font-bold mb-6">
          Start Your Journey to Financial Freedom
        </h2>
        <p className="text-slate-400 mb-8">
          Join thousands of clients who have successfully improved their credit 
          with our ethical, transparent, and legally-compliant services.
        </p>

        <ul className="space-y-4">
          {benefits.map((benefit, index) => (
            <li key={`benefit-${benefit.slice(0,10)}`} className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-teal-400" />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <Shield className="h-10 w-10 text-amber-700" strokeWidth={1.5} />
            <div className="text-center">
              <h1 className="font-heading text-2xl font-bold text-slate-900">Tamperis Solutions</h1>
              <p className="text-sm text-amber-700">From Faith to Freedom</p>
            </div>
          </div>

          <Card className="border-slate-200 shadow-xl" data-testid="register-card">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl">
                {showPackages ? 'Choose Your Package' : 'Get Started Today'}
              </CardTitle>
              <CardDescription>
                {showPackages 
                  ? 'Select the restoration package that fits your needs'
                  : 'Create your account to begin your credit restoration journey'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!showPackages ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="John"
                          className="pl-10"
                          data-testid="first-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Doe"
                          className="pl-10"
                          data-testid="last-name-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john.doe@example.com"
                        className="pl-10"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        placeholder="Min. 6 characters"
                        className="pl-10"
                        data-testid="password-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700 mt-6"
                    disabled={loading}
                    data-testid="continue-btn"
                  >
                    {loading ? 'Creating Account...' : 'Create Account & Continue'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  {/* Document Requirements Notice */}
                  <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <h4 className="font-semibold text-slate-900 text-sm mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-amber-600" />
                      Required Documents
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      To help us serve you better and ensure the credit bureaus can verify your identity, 
                      please be prepared to submit the following documents after purchase:
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Valid Photo ID</strong> — Current driver's license or government-issued ID</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Proof of Residence</strong> — Utility bill, bank statement, or similar document</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-3 w-3 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Social Security</strong> — SSN card or last 4 digits if card is unavailable</span>
                      </li>
                    </ul>
                    <p className="mt-2 text-xs text-amber-700 font-medium">
                      Please ensure all documents are clear, legible, and fully visible.
                    </p>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  {/* Client Info Display */}
                  <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-sm text-teal-800">
                      <span className="font-semibold">Welcome, {displayName}!</span>
                      <br />
                      <span className="text-teal-600">{displayEmail}</span>
                    </p>
                  </div>

                  {/* Package Selection */}
                  <div className="space-y-4">
                    {/* Basic Package */}
                    <a 
                      href={getStripeUrl('https://buy.stripe.com/fZu14m5QG9qa2It0LNco013')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid="basic-package-btn"
                    >
                      <div className="p-4 border-2 border-slate-200 rounded-lg hover:border-slate-400 transition-colors cursor-pointer">
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-semibold text-slate-900">Basic Restoration</h3>
                            <p className="text-sm text-slate-600">Up to 3 months service</p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-slate-900">$297</span>
                            <p className="text-xs text-slate-500">one-time</p>
                          </div>
                        </div>
                      </div>
                    </a>

                    {/* Premium Package */}
                    <a 
                      href={getStripeUrl('https://buy.stripe.com/00weVcencbyi6YJ3XZco014')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid="premium-package-btn"
                    >
                      <div className="p-4 border-2 border-amber-300 rounded-lg bg-amber-50 hover:border-amber-500 transition-colors cursor-pointer relative">
                        <div className="absolute -top-2 left-4">
                          <span className="bg-amber-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                            BEST VALUE
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-semibold text-slate-900">Premium Restoration</h3>
                            <p className="text-sm text-slate-600">6 months comprehensive service</p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-amber-700">$497</span>
                            <p className="text-xs text-slate-500">one-time</p>
                          </div>
                        </div>
                      </div>
                    </a>

                    {/* Nurses Program */}
                    <a 
                      href={getStripeUrl('https://buy.stripe.com/fZubJ04MC31Mfvf8efco015')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                      data-testid="nurses-package-btn"
                    >
                      <div className="p-4 border-2 border-teal-300 rounded-lg bg-teal-50 hover:border-teal-500 transition-colors cursor-pointer relative">
                        <div className="absolute -top-2 left-4">
                          <span className="bg-teal-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Stethoscope className="h-3 w-3" />
                            FOR NURSES
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-semibold text-slate-900">Nurses Homeownership Program</h3>
                            <p className="text-sm text-slate-600">9-month comprehensive service</p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-teal-700">$797</span>
                            <p className="text-xs text-slate-500">one-time</p>
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>

                  <p className="text-xs text-center text-slate-500">
                    + $29/month credit monitoring required for all packages
                  </p>

                  {/* Dashboard Link */}
                  {isAuthenticated() && (
                    <div className="text-center">
                      <Link to="/dashboard" className="text-sm text-amber-700 hover:text-amber-800 font-medium">
                        Go to Dashboard →
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Login Link */}
              {!showPackages && (
                <p className="text-center text-sm text-slate-600 mt-6">
                  Already a client?{' '}
                  <Link to="/login" className="text-amber-700 hover:text-amber-800 font-medium">
                    Sign in here
                  </Link>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Trust Badges */}
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Shield className="h-4 w-4" /> Secure & Encrypted
            </span>
            <span>FCRA Compliant</span>
            <span>Metro 2® Certified</span>
          </div>
        </div>
      </div>
    </div>
  );
}
